import { PointLight, SpotLight, Vector3 } from '@babylonjs/core';
import { GameConfig } from './GameConfig';
import { Level } from './Level';

export interface PathLight {
  light: PointLight | SpotLight;
  position: Vector3;
  pathIndex: number;
}

export class PathLightGenerator {
  constructor(private level: Level) {}

  lights: PathLight[] = [];

  generate(): void {
    const config = this.level.config.pathLights;
    if (!config) {
      return;
    }

    const interval = config.interval || 1;

    if (!interval || interval <= 0) {
      return;
    }

    const pathPoints = this.level.pathPoints;

    // Start after first interval, stop before end
    for (let i = interval; i < pathPoints.length - interval; i += interval) {
      const probability = this.level.rng.uFloat32();
      if (config.probability && probability > config.probability) {
        continue;
      }
      this.lights.push(this.createLight(i));
    }

    if (GameConfig.logDebug) {
      console.log(`[PathLightGenerator] Created ${this.lights.length} lights`);
    }
  }

  private createLight(pathIndex: number): PathLight {
    const config = this.level.config.pathLights;
    const pathInfo = this.level.getPathInfo(pathIndex);

    const position = pathInfo.pathPoint.add(config.offset || Vector3.Zero());

    let light: PointLight | SpotLight;
    if (config.spot) {
      const distance = Vector3.Distance(position, pathInfo.pathPoint);
      const targetWidth = this.level.config.path.laneWidth || 4;
      let direction: Vector3 = config.direction ?? pathInfo.pathPoint.subtract(position).normalize();
      const angle: number = config.angle ?? 2 * Math.atan(targetWidth / 2 / distance);
      // Calculate exponent so the light is focused enough to reach the point effectively
      const exponent: number = config.exponent ?? distance * 2;

      light = new SpotLight('spotLight' + pathIndex, position, direction, angle, exponent, this.level.scene);
      light.intensity = config.intensity * 1000;
    } else {
      light = new PointLight('pointLight' + pathIndex, position, this.level.scene);
      light.intensity = config.intensity;
    }

    const color = this.level.randomArrayElement(config.colors);
    light.diffuse = color;

    return { light, position, pathIndex };
  }

  update() {}

  dispose() {}
}
